import {
  Fragment,
  useCallback,
  useMemo,
  useState,
  type ImgHTMLAttributes,
} from "react";
import {
  advanceConferenceLogoCandidate,
  conferenceLogoCandidates,
} from "./conference-brand";

export interface ConferenceBrandImageProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "alt" | "onError" | "onLoad" | "src"
> {
  readonly conferenceId: string;
  readonly conferenceName: string;
  readonly configuredLogo: string | null;
  readonly compact?: boolean;
  readonly alt?: string;
  readonly fallbackClassName?: string;
}

function conferenceMonogram(name: string): string {
  const normalized = name.trim();
  return normalized ? normalized.slice(0, 2).toUpperCase() : "--";
}

export function ConferenceBrandImage({
  conferenceId,
  configuredLogo,
  compact = false,
  ...props
}: ConferenceBrandImageProps) {
  const candidates = useMemo(
    () => conferenceLogoCandidates(conferenceId, configuredLogo, compact),
    [compact, conferenceId, configuredLogo],
  );
  // Reset only when the actual source list changes. A passive mount effect can
  // run after a memory-cache load event and hide an already loaded image.
  return (
    <ResolvedConferenceBrandImage
      {...props}
      key={JSON.stringify(candidates)}
      candidates={candidates}
    />
  );
}

function ResolvedConferenceBrandImage({
  candidates,
  conferenceName,
  alt = "",
  fallbackClassName,
  ...imageProps
}: Omit<
  ConferenceBrandImageProps,
  "conferenceId" | "configuredLogo" | "compact"
> & { readonly candidates: readonly string[] }) {
  const [candidateIndex, setCandidateIndex] = useState<number | null>(
    candidates.length ? 0 : null,
  );
  const [loadedSource, setLoadedSource] = useState<string | null>(null);

  const source =
    candidateIndex === null ? null : (candidates[candidateIndex] ?? null);
  const imageRef = useCallback(
    (image: HTMLImageElement | null) => {
      // A cached image can already be complete when React attaches the ref.
      if (image?.complete && image.naturalWidth > 0) setLoadedSource(source);
    },
    [source],
  );
  const loaded = source !== null && loadedSource === source;
  const monogramClassName = [
    "conference-brand-image__monogram",
    fallbackClassName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Fragment>
      {!loaded ? (
        <span className={monogramClassName} aria-hidden="true">
          {conferenceMonogram(conferenceName)}
        </span>
      ) : null}
      {source ? (
        <img
          {...imageProps}
          key={source}
          ref={imageRef}
          src={source}
          alt={alt}
          style={{ ...imageProps.style, display: loaded ? undefined : "none" }}
          onLoad={() => setLoadedSource(source)}
          onError={() => {
            setCandidateIndex((currentIndex) =>
              currentIndex === null || candidates[currentIndex] !== source
                ? currentIndex
                : advanceConferenceLogoCandidate(
                    currentIndex,
                    candidates.length,
                  ),
            );
          }}
        />
      ) : null}
    </Fragment>
  );
}
